import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import type { File as SlabFile } from '@core/types/api/File/File';
import { hasValue } from '@core/utils/equality';
import { useSlabQuery } from '@web/hooks/useSlabQuery';

export type UseFileDownloadOptions = {
  onError?: (error: unknown) => void;
};

export type UseFileDownloadResult = {
  requestDownload: (file: SlabFile) => void;
  isDownloading: boolean;
  downloadingFileId: string | null;
};

export const useFileDownload = ({
  onError,
}: UseFileDownloadOptions = {}): UseFileDownloadResult => {
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Ref so the error effect always calls the latest handler without taking a
  // possibly-unstable consumer callback into its dependency array.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const { data, isFetching, isError, error } = useSlabQuery(
    'GET file download url by file ID',
    { pathParams: { id: downloadingFileId ?? '' } },
    { enabled: hasValue(downloadingFileId), staleTime: 0, gcTime: 0 },
  );

  const clearDownloadUrlCache = (fileId: string): void => {
    queryClient.removeQueries({
      predicate: (query) =>
        query.queryKey[1] === 'GET file download url by file ID' &&
        typeof query.queryKey[0] === 'string' &&
        query.queryKey[0].endsWith(`/${fileId}/download`),
    });
  };

  useEffect(() => {
    if (!hasValue(data) || !hasValue(downloadingFileId)) {
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = data.url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    clearDownloadUrlCache(downloadingFileId);
    setDownloadingFileId(null);
  }, [data, downloadingFileId]);

  useEffect(() => {
    if (!isError || !hasValue(downloadingFileId)) {
      return;
    }

    // Requesting the URL failed outright -- reset so the user can retry instead of
    // getting stuck with the query permanently disabled.
    clearDownloadUrlCache(downloadingFileId);
    setDownloadingFileId(null);
    onErrorRef.current?.(error);
  }, [isError, error, downloadingFileId]);

  return {
    requestDownload: (file: SlabFile): void => setDownloadingFileId(file.id),
    isDownloading: isFetching,
    downloadingFileId,
  };
};
